import { useCallback, useMemo, useState } from 'react';
import { t } from '@ping/helpers';
import { Select } from '@ping/uikit/SelectA11Y';
import { getExchangeType, isExchangeTypeEnabled } from '@ping/utils/checkCompany';
import { NoMarketsAvailableModal } from '@ping/components/NoMarketsAvailableModal';
import { useMobile } from '@ping/hooks';
import CloseIcon from '@ping/assets/Icon/close.svg';

import style from './style.module.scss';

const CommoditiesIcon = () => (
  <svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M12 2L2 7L12 12L22 7L12 2Z'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path d='M2 17L12 22L22 17' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
    <path d='M2 12L12 17L22 12' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
);

const CryptoIcon = () => (
  <svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <circle cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
    <path
      d='M12 6 L16 12 L12 18 L8 12 Z'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      fill='none'
    />
    <line x1='8' y1='12' x2='16' y2='12' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
  </svg>
);

const StockIcon = () => (
  <svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path d='M3 3v18h18' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
    <path d='M7 16l4-4 3 3 5-8' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
);

const ForexIcon = () => (
  <svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <circle cx='9' cy='12' r='7' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
    <circle cx='15' cy='12' r='7' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
    <path d='M9 5v14' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
    <path d='M15 5v14' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
);

interface ExchangeOption {
  key: string;
  label: string;
  icon: JSX.Element;
  storageValue: string | null;
}

export const ExchangeTypeDropdown = () => {
  const [currentType, setMarketPlace] = useState(getExchangeType());
  const isMobile = useMobile();
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);

  // All available exchange options (always include all to properly detect current state)
  const allExchangeOptions: ExchangeOption[] = useMemo(() => {
    return [
      {
        key: 'crypto',
        label: t('Crypto Currency'),
        icon: <CryptoIcon />,
        storageValue: null,
      },
      {
        key: 'ping',
        label: t('Digital Commodities'),
        icon: <CommoditiesIcon />,
        storageValue: 'ping',
      },
      {
        key: 'stock',
        label: t('Digital Stocks'),
        icon: <StockIcon />,
        storageValue: 'stock',
      },
      {
        key: 'forex',
        label: t('Digital Forex'),
        icon: <ForexIcon />,
        storageValue: 'forex',
      },
    ];
  }, []);

  const handleSelectionChange = useCallback(
    (key: React.Key | string) => {
      const keyStr = String(key);
      const selectedOption = allExchangeOptions.find(opt => opt.key === keyStr);
      if (!selectedOption) return;

      if (selectedOption.storageValue === null) {
        localStorage.removeItem('company');
      } else {
        localStorage.setItem('company', selectedOption.storageValue);
      }
      if (!isExchangeTypeEnabled(keyStr)) {
        setShowComingSoonModal(true);
        return;
      }
      setMarketPlace(keyStr);
      // Dispatch custom event for same-window listeners
      window.dispatchEvent(new CustomEvent('exchangeTypeChanged'));

      window.location.href = '/';
    },
    [allExchangeOptions]
  );

  // Always use allExchangeOptions to find current option, so it works even when filtered out
  const currentOption = allExchangeOptions.find(opt => opt.key === currentType) || allExchangeOptions[0];

  return (
    <>
      {isMobile ? (
        <>
          <button
            className={style['exchange-type-dropdown-mobile-button']}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              setIsMobileModalOpen(true);
            }}
            aria-label={t('Select exchange type')}
            type='button'
          >
            <div className={style['icon']}>{currentOption.icon}</div>
          </button>

          {isMobileModalOpen && (
            <div className={style['mobile-modal-overlay']} onClick={() => setIsMobileModalOpen(false)}>
              <div className={style['mobile-modal']} onClick={e => e.stopPropagation()}>
                <div className={style['mobile-modal-header']}>
                  <h3>{t('Select Exchange Type')}</h3>
                  <button onClick={() => setIsMobileModalOpen(false)} className={style['close-button']} type='button'>
                    <CloseIcon />
                  </button>
                </div>
                <div className={style['mobile-modal-content']}>
                  {allExchangeOptions.map(option => {
                    return (
                      <button
                        key={option.key}
                        className={style['mobile-option']}
                        type='button'
                        onClick={() => {
                          handleSelectionChange(option.key);
                          setIsMobileModalOpen(false);
                        }}
                      >
                        <div className={style['mobile-option-icon']}>{option.icon}</div>
                        <span className={style['mobile-option-label']}>{option.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <Select
          wrapperClassName={style['exchange-type-dropdown']}
          className={style['exchange-dropdown-panel']}
          items={allExchangeOptions}
          selectedKey={currentOption.key}
          onSelectionChange={handleSelectionChange}
          aria-label={t('Select exchange type')}
          buttonContent={
            <div className={style['dropdown-button-content']}>
              <div className={style['icon']}>{currentOption.icon}</div>
              <span className={style['text']}>{currentOption.label}</span>
            </div>
          }
        >
          {item => {
            return (
              <Select.Item key={item.key} aria-label={item.label}>
                <div className={style['dropdown-item']}>
                  <div className={style['item-icon']}>{item.icon}</div>
                  <span className={style['item-label']}>{item.label}</span>
                </div>
              </Select.Item>
            );
          }}
        </Select>
      )}

      <NoMarketsAvailableModal isOpen={showComingSoonModal} onClose={() => setShowComingSoonModal(false)} />
    </>
  );
};
